import bindAll from 'lodash.bindall';
import path from 'path';
import PropTypes from 'prop-types';
import React from 'react';
import VM from 'scratch-vm';
import {defineMessages, injectIntl, intlShape} from 'react-intl';
import log from '../lib/log';

import extensionLibraryContent, {
    galleryError,
    galleryLoading,
    galleryMore
} from '../lib/libraries/extensions/index.jsx';
import extensionTags from '../lib/libraries/tw-extension-tags';
import {openwarpLibrary, openwarpLibraryWarning} from '../lib/libraries/openwarp/openwarp-extensions';

import LibraryComponent from '../components/library/library.jsx';
import extensionIcon from '../components/action-menu/icon--sprite.svg';

// Inline fallback icon (data: URL) so local extensions always have a visible
// preview image even when no icon file is present and regardless of the
// desktop tw-editor:// protocol's handling of relative asset URLs.
const DEFAULT_EXTENSION_ICON = 'data:image/svg+xml,' + encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120">' +
    '<rect width="120" height="120" rx="16" fill="#4c97ff"/>' +
    '<text x="60" y="74" font-family="sans-serif" font-size="38" font-weight="bold" fill="#ffffff" text-anchor="middle">EXT</text>' +
    '</svg>'
);
import TagButton from '../components/tag-button/tag-button.jsx';
import AddExtensionLibraryModal from '../components/tw-extension-library-modal/tw-extension-library-modal.jsx';
import libraryStyles from '../components/library/library.css';

const LIBRARIES_STORAGE_KEY = 'tw:loaded-extension-libraries';

// Derive a short display name from a library URL by stripping the
// protocol, "www.", path, and the top-level domain.
// e.g. "https://openwarp-extensions.pages.dev/" -> "openwarp-extensions.pages"
const getLibraryName = rawURL => {
    let name = (rawURL || '').trim();
    name = name.replace(/^https?:\/\//i, '');
    name = name.replace(/^www\./i, '');
    name = name.replace(/\/.*$/, '');
    name = name.replace(/\.[^.]+$/, '');
    return name || rawURL;
};

const readLibraries = () => {
    try {
        const data = JSON.parse(localStorage.getItem(LIBRARIES_STORAGE_KEY));
        if (!Array.isArray(data)) {
            return [];
        }
        // Keep only well-formed library entries, and sanitize their extensions:
        // discard any persisted `credits` (older versions stored React elements
        // there, which become invalid plain objects after JSON round-tripping)
        // and rely on the serializable `by` field to rebuild them at render time.
        return data
            .filter(library => library && Array.isArray(library.extensions))
            .map(library => ({
                ...library,
                extensions: library.extensions.map(extension => {
                    const by = Array.isArray(extension.by) ? extension.by : [];
                    const {
                        // eslint-disable-next-line no-unused-vars
                        credits, ...rest
                    } = extension;
                    return {...rest, by};
                })
            }));
    } catch (e) {
        return [];
    }
};

const writeLibraries = libraries => {
    try {
        // Local extension libraries are backed by in-memory data: URLs that are
        // too large and not restorable across sessions, so never persist them.
        // Built-in libraries are always reconstructed in memory, so skip them too.
        localStorage.setItem(LIBRARIES_STORAGE_KEY, JSON.stringify(libraries.filter(l => !l.local && !l.builtin)));
    } catch (e) {
        // ignore
    }
};

const messages = defineMessages({
    extensionTitle: {
        defaultMessage: 'Choose an Extension',
        description: 'Heading for the extension library',
        id: 'gui.extensionLibrary.chooseAnExtension'
    }
});

const toLibraryItem = extension => {
    if (typeof extension === 'object') {
        const item = {
            rawURL: extension.iconURL || extensionIcon,
            ...extension
        };
        // Build the credits list (which contains React elements) at render time
        // so we never serialize React elements into localStorage.
        if (item.by && !item.credits) {
            item.credits = (item.by || []).map(credit => (credit && credit.link ?
                <a
                    href={credit.link}
                    target="_blank"
                    rel="noreferrer"
                    key={credit.name}
                >
                    {credit.name}
                </a> :
                (credit && credit.name) || credit));
        }
        return item;
    }
    return extension;
};

const translateGalleryItem = (extension, locale) => ({
    ...extension,
    name: extension.nameTranslations[locale] || extension.name,
    description: extension.descriptionTranslations[locale] || extension.description
});

let cachedGallery = null;

const fetchLibrary = async () => {
    const res = await fetch('https://extensions.turbowarp.org/generated-metadata/extensions-v0.json');
    if (!res.ok) {
        throw new Error(`HTTP status ${res.status}`);
    }
    const data = await res.json();
    return data.extensions.map(extension => ({
        name: extension.name,
        nameTranslations: extension.nameTranslations || {},
        description: extension.description,
        descriptionTranslations: extension.descriptionTranslations || {},
        extensionId: extension.id,
        extensionURL: `https://extensions.turbowarp.org/${extension.slug}.js`,
        iconURL: `https://extensions.turbowarp.org/${extension.image || 'images/unknown.svg'}`,
        tags: ['tw'],
        credits: [
            ...(extension.original || []),
            ...(extension.by || [])
        ].map(credit => {
            if (credit.link) {
                return (
                    <a
                        href={credit.link}
                        target="_blank"
                        rel="noreferrer"
                        key={credit.name}
                    >
                        {credit.name}
                    </a>
                );
            }
            return credit.name;
        }),
        docsURI: extension.docs ? `https://extensions.turbowarp.org/${extension.slug}` : null,
        samples: extension.samples ? extension.samples.map(sample => ({
            href: `${process.env.ROOT}editor?project_url=https://extensions.turbowarp.org/samples/${encodeURIComponent(sample)}.sb3`,
            text: sample
        })) : null,
        incompatibleWithScratch: !extension.scratchCompatible,
        featured: true
    }));
};

class ExtensionLibrary extends React.PureComponent {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleItemSelect',
            'handleOpenAddModal',
            'handleCloseAddModal',
            'handleChangeURL',
            'handleKeyDown',
            'handleLoadLibrary',
            'handleSelectLocal',
            'handleChangeLocalName',
            'loadBuiltLibrary',
            'loadDirectoryLibrary',
            'handleChipClick',
            'handleChipRemove',
            'handleTagClick',
            'loadCustomExtension'
        ]);
        this.state = {
            gallery: cachedGallery,
            galleryError: null,
            galleryTimedOut: false,
            addModalOpen: false,
            url: '',
            urlError: false,
            localName: '',
            libraries: [openwarpLibrary].concat(readLibraries()),
            loadedURLs: [],
            selectedLibrary: null
        };
    }
    componentDidMount () {
        if (!this.state.gallery) {
            const timeout = setTimeout(() => {
                this.setState({
                    galleryTimedOut: true
                });
            }, 750);

            fetchLibrary()
                .then(gallery => {
                    cachedGallery = gallery;
                    this.setState({
                        gallery
                    });
                    clearTimeout(timeout);
                })
                .catch(error => {
                    log.error(error);
                    this.setState({
                        galleryError: error
                    });
                    clearTimeout(timeout);
                });
        }
    }
    handleItemSelect (item) {
        if (item.href) {
            return;
        }

        const extensionId = item.extensionId;

        if (extensionId === 'custom_extension') {
            this.props.onOpenCustomExtensionModal();
            return;
        }

        if (extensionId === 'procedures_enable_return') {
            this.props.onEnableProcedureReturns();
            this.props.onCategorySelected('myBlocks');
            return;
        }

        const url = item.extensionURL ? item.extensionURL : extensionId;
        if (!item.disabled) {
            if (this.props.vm.extensionManager.isExtensionLoaded(extensionId)) {
                this.props.onCategorySelected(extensionId);
            } else {
                this.props.vm.extensionManager.loadExtensionURL(url)
                    .then(() => {
                        this.props.onCategorySelected(extensionId);
                    })
                    .catch(err => {
                        log.error(err);
                        // eslint-disable-next-line no-alert
                        alert(err);
                    });
            }
        }
    }
    handleOpenAddModal () {
        this.setState({
            addModalOpen: true,
            url: '',
            urlError: false,
            localName: ''
        });
    }
    handleCloseAddModal () {
        this.setState({
            addModalOpen: false,
            url: '',
            urlError: false,
            localName: ''
        });
    }
    handleChangeURL (event) {
        this.setState({
            url: event.target.value,
            urlError: false
        });
    }
    handleChangeLocalName (event) {
        this.setState({
            localName: event.target.value
        });
    }
    handleKeyDown (event) {
        if (event.key === 'Enter') {
            this.handleLoadLibrary();
        }
    }
    handleSelectLocal (input) {
        // Input is either:
        //  - a DOM event from <input type="file" webkitdirectory> (website build)
        //  - {root, files} returned by the desktop native folder picker
        //  - a plain array of {relativePath, content} (legacy)
        let root = null;
        if (input && !input.target && Array.isArray(input.files)) {
            root = input.root || null;
            input = input.files;
        }
        let entries;
        if (Array.isArray(input)) {
            // Desktop: array of {relativePath, content, iconDataURL, name, description, id, by}
            // from the folder picker. These fields are parsed from the extension's
            // leading comment block by the main process so the card matches the
            // native gallery data model (name/description/by from // Name:/// Description:/// By:).
            entries = input.map(f => ({
                relativePath: f.relativePath,
                iconDataURL: f.iconDataURL,
                name: f.name,
                description: f.description,
                id: f.id,
                by: Array.isArray(f.by) ? f.by : [],
                get text () {
                    return Promise.resolve(f.content);
                },
                size: (f.content || '').length
            }));
        } else {
            const files = Array.from(input.target.files || []);
            // Reset so selecting the same folder again re-triggers onChange.
            input.target.value = '';
            entries = files.map(f => ({
                relativePath: f.webkitRelativePath || f.name,
                iconDataURL: null,
                text: f.text(),
                size: f.size
            }));
        }
        // Skip build/dev/config files so only real extensions become cards.
        const skipDir = /(^|\/)(node_modules|development|build-snippets|build|dist|scripts|src|\.git)(\/|$)/i;
        const isConfig = /(^|\/)(webpack.*\.config\.js|\.eslintrc.*|eslint\.config\.js|.*\.config\.js|rollup\.config\.js|babel\.config\.js)$/i;
        const jsFiles = entries.filter(f => {
            const rel = f.relativePath;
            if (!/\.js$/i.test(rel)) return false;
            if (skipDir.test(rel)) return false;
            if (isConfig.test(rel)) return false;
            return true;
        });
        if (jsFiles.length === 0) {
            this.setState({urlError: true});
            // eslint-disable-next-line no-alert
            alert('No extension .js files found in that folder. Build scripts and config files are ignored.');
            return;
        }
        // Single file size guard (2 MB) to avoid freezing on huge bundles.
        const MAX = 2 * 1024 * 1024;
        const oversized = jsFiles.filter(f => f.size > MAX);
        const usable = jsFiles.filter(f => f.size <= MAX);
        if (usable.length === 0) {
            // eslint-disable-next-line no-alert
            alert('All files are too large to load (max 2 MB each).');
            return;
        }
        if (oversized.length > 0) {
            log.warn(`Skipped ${oversized.length} files over 2 MB`);
        }
        // Read every local .js file into a data: URL. data: URLs are accepted by
        // the extension manager and run unsandboxed once manually trusted.
        Promise.all(usable.map(async file => {
            const text = await file.text;
            const base64 = btoa(unescape(encodeURIComponent(text)));
            const dataURL = `data:text/javascript;base64,${base64}`;
            const rel = file.relativePath;
            const slug = rel.replace(/\.js$/i, '').split(/[/\\]/).pop();
            const iconURL = file.iconDataURL || extensionIcon;
            // Prefer the metadata parsed from the extension's leading comment block
            // (// Name:, // Description:, // By:) so the card matches the native
            // gallery; fall back to the file slug when the header is missing.
            // On the website build the desktop main process is unavailable, so the
            // comment block is parsed directly here (matching TurboWarp's model).
            const header = parseExtensionHeader(text);
            const name = file.name || header.name || slug;
            const description = file.description || header.description || '';
            const by = (Array.isArray(file.by) && file.by.length > 0) ?
                file.by : (header.by || []);
            const extensionId = file.id || slug;
            return {
                name: name,
                description: description,
                by: by,
                extensionId: extensionId,
                extensionURL: dataURL,
                rawURL: dataURL,
                iconURL: iconURL,
                tags: ['tw'],
                featured: true
            };
        })).then(extensions => {
            let folderName;
            let folderPath;
            if (root) {
                folderPath = root;
                folderName = path.basename(folderPath);
            } else if (jsFiles[0] && jsFiles[0].relativePath) {
                folderName = jsFiles[0].relativePath.split(/[/\\]/).filter(Boolean)[0] || '本地扩展';
            } else {
                folderName = '本地扩展';
            }
            // Use the custom name typed in the modal (if any), otherwise fall back
            // to the folder name.
            const customName = (this.state.localName || '').trim();
            const libraryName = customName || folderName;
            const libKey = `local:${folderName}`;
            const library = {url: libKey, name: libraryName, extensions, local: true, folderPath};
            const libraries = this.state.libraries.filter(l => l.url !== libKey);
            libraries.push(library);
            this.setState({
                libraries,
                addModalOpen: false,
                url: '',
                urlError: false,
                localName: ''
            });
            writeLibraries(libraries);
        }).catch(err => {
            log.error(err);
            this.setState({urlError: true});
            // eslint-disable-next-line no-alert
            alert(err.message || err);
        });
    }
    handleLoadLibrary () {
        const rawURL = this.state.url.trim();
        if (!rawURL) {
            this.setState({urlError: true});
            return;
        }
        const name = getLibraryName(rawURL);

        // A local filesystem path (e.g. D:\...\build or \\server\share) cannot
        // be fetched as a URL on the desktop tw-editor:// protocol. Route it to
        // the main process which reads the folder's manifest (built or unbuilt)
        // and returns the parsed extension files.
        const isLocalPath = /^[a-zA-Z]:[\\/]/.test(rawURL) || /^\\\\/.test(rawURL) ||
            (/^[\\/]/.test(rawURL) && !/^https?:/i.test(rawURL));
        if (isLocalPath) {
            if (window.EditorPreload && window.EditorPreload.readExtensionFolder) {
                this.setState({urlError: false});
                window.EditorPreload.readExtensionFolder(rawURL)
                    .then(result => {
                        if (!result || !Array.isArray(result.files) || result.files.length === 0) {
                            throw new Error(`Could not load extension library at ${rawURL}. Make sure it contains an extensions.json or generated-metadata/extensions-v0.json manifest.`);
                        }
                        const extensions = result.files
                            .filter(f => /\.js$/i.test(f.relativePath))
                            .map(f => {
                                const base64 = Buffer.from(f.content, 'utf-8').toString('base64');
                                const dataURL = `data:text/javascript;base64,${base64}`;
                                const slug = f.relativePath.replace(/\.js$/i, '').split(/[/\\]/).pop();
                                return {
                                    name: f.name || slug,
                                    description: f.description || '',
                                    by: (f.by || []),
                                    extensionId: f.id || slug,
                                    extensionURL: dataURL,
                                    rawURL: dataURL,
                                    iconURL: f.iconDataURL || DEFAULT_EXTENSION_ICON,
                                    insetIconURL: (f.insetIconURL && /^https?:|^data:/.test(f.insetIconURL)) ?
                                        f.insetIconURL : '',
                                    docsURI: f.docsURI || '',
                                    samples: (Array.isArray(f.samples) && f.samples.length) ? f.samples : undefined,
                                    bluetoothRequired: !!f.bluetoothRequired,
                                    internetConnectionRequired: !!f.internetConnectionRequired,
                                    tags: ['tw'],
                                    featured: true
                                };
                            });
                        const library = {
                            url: rawURL,
                            name,
                            manifestURL: rawURL,
                            extensions,
                            local: true,
                            folderPath: result.root || rawURL
                        };
                        const libraries = this.state.libraries.filter(l => l.url !== rawURL);
                        libraries.push(library);
                        this.setState({
                            libraries,
                            addModalOpen: false,
                            url: '',
                            urlError: false
                        });
                        writeLibraries(libraries);
                    })
                    .catch(err => {
                        log.error(err);
                        this.setState({urlError: true});
                        // eslint-disable-next-line no-alert
                        alert(err.message || err);
                    });
                return;
            }
            this.setState({urlError: true});
            // eslint-disable-next-line no-alert
            alert('Local extension libraries require the desktop app.');
            return;
        }

        // Normalize the entered URL into a manifest URL. If the user gave a
        // bare library base (e.g. https://openwarp-extensions.pages.dev/) we look for
        // the standard generated-metadata manifest used by TurboWarp-style
        // extension galleries.
        let manifestURL = rawURL;
        if (!/\.json(\?|#|$)/i.test(manifestURL)) {
            manifestURL = manifestURL.replace(/\/+$/, '');
            manifestURL += '/generated-metadata/extensions-v0.json';
        }

        this.setState({urlError: false});

        // Prefer a "built" library with a generated-metadata manifest. If that
        // is unavailable (the library is "unbuilt" / only a directory of .js
        // files), fall back to scanning the directory listing for raw .js
        // extension files.
        this.loadBuiltLibrary(manifestURL)
            .catch(() => this.loadDirectoryLibrary(rawURL))
            .then(extensions => {
                const library = {url: rawURL, name, manifestURL, extensions};
                const libraries = this.state.libraries.filter(l => l.url !== rawURL);
                libraries.push(library);
                this.setState({
                    libraries,
                    addModalOpen: false,
                    url: '',
                    urlError: false
                });
                writeLibraries(libraries);
            })
            .catch(err => {
                log.error(err);
                this.setState({urlError: true});
                // eslint-disable-next-line no-alert
                alert(err.message || err);
            });
    }
    // Load a "built" extension library described by a generated-metadata
    // manifest. Resolves with an array of serializable extension items, or
    // rejects if the manifest is missing/invalid.
    loadBuiltLibrary (manifestURL) {
        return fetch(manifestURL)
            .then(res => {
                if (!res.ok) {
                    throw new Error(`HTTP status ${res.status}`);
                }
                return res.json();
            })
            .then(data => {
                if (!data || !Array.isArray(data.extensions)) {
                    throw new Error('Invalid extension library manifest.');
                }
                // Derive the library base URL that the manifest's extensions are
                // served from. The manifest is conventionally located at
                // <base>/generated-metadata/extensions-v0.json while the actual
                // .js files and images live directly under <base>. <base> may be
                // a subpath (e.g. /extensions/) rather than the origin root, which
                // is why we must not simply use the origin here.
                let libraryBase;
                const suffix = '/generated-metadata/extensions-v0.json';
                if (manifestURL.toLowerCase().endsWith(suffix)) {
                    libraryBase = manifestURL.slice(0, -suffix.length);
                } else {
                    libraryBase = manifestURL.replace(/\/[^/]*$/, '');
                }
                return data.extensions.map(extension => {
                    const extId = extension.id || extension.slug;
                    const slug = extension.slug || extension.id;
                    const iconURL = /^https?:\/\//i.test(extension.image || '') ?
                        extension.image :
                        `${libraryBase}/${extension.image || 'images/unknown.svg'}`;
                    const extensionURL = `${libraryBase}/${slug}.js`;
                    // Store only serializable data here. The credits array (which
                    // contains React elements) is built at render time in
                    // toLibraryItem, so it is never persisted to localStorage.
                    return {
                        name: extension.name,
                        description: extension.description,
                        by: extension.by || [],
                        extensionId: extId,
                        extensionURL,
                        rawURL: iconURL,
                        iconURL,
                        tags: ['tw'],
                        featured: true
                    };
                });
            });
    }
    // Load an "unbuilt" extension library by scanning the directory listing at
    // the library base URL for raw .js extension files. Resolves with an array
    // of serializable extension items, or rejects if no .js files are found.
    loadDirectoryLibrary (rawURL) {
        const baseURL = rawURL.replace(/\/+$/, '') + '/';
        return fetch(baseURL)
            .then(res => {
                if (!res.ok) {
                    throw new Error(`HTTP status ${res.status}`);
                }
                return res.text();
            })
            .then(html => {
                const doc = new DOMParser().parseFromString(html, 'text/html');
                const seen = new Set();
                const extensions = [];
                doc.querySelectorAll('a[href]').forEach(a => {
                    const href = a.getAttribute('href');
                    if (!href || !/\.js(\?|#|$)/i.test(href)) {
                        return;
                    }
                    let url;
                    try {
                        url = new URL(href, baseURL).href;
                    } catch (e) {
                        return;
                    }
                    if (seen.has(url)) {
                        return;
                    }
                    seen.add(url);
                    const fileName = decodeURIComponent(href.split('/').pop().split(/[?#]/)[0]);
                    const slug = fileName.replace(/\.js$/i, '');
                    const linkText = (a.textContent || '').trim();
                    const displayName = (linkText && linkText !== fileName) ? linkText : slug;
                    extensions.push({
                        name: displayName,
                        description: '',
                        by: [],
                        extensionId: slug,
                        extensionURL: url,
                        rawURL: extensionIcon,
                        iconURL: extensionIcon,
                        tags: ['tw'],
                        featured: true
                    });
                });
                if (extensions.length === 0) {
                    throw new Error('No .js extension files found in this directory.');
                }
                return extensions;
            });
    }
    loadCustomExtension (url, extensionId = url) {
        if (this.props.vm.extensionManager.isExtensionLoaded(extensionId)) {
            this.props.onCategorySelected(extensionId);
            return;
        }
        this.props.vm.extensionManager.loadExtensionURL(url)
            .then(() => {
                this.setState({
                    loadedURLs: this.state.loadedURLs.concat(url)
                });
                this.props.onCategorySelected(extensionId);
            })
            .catch(err => {
                log.error(err);
                // eslint-disable-next-line no-alert
                alert(err);
            });
    }
    handleChipClick (library) {
        // The built-in "OpenWarp 编辑器" library hosts native file/admin
        // extensions that are incompatible with TurboWarp and other editors.
        // Show a warning before letting the user into the library.
        if (library.builtin) {
            // eslint-disable-next-line no-alert
            if (!window.confirm(`${openwarpLibraryWarning}\n\n确定要继续吗？`)) {
                return;
            }
        }
        // Toggle the grid filter so the grid jumps to this library's extensions,
        // just like clicking a built-in tag (e.g. "Sound") filters the gallery.
        this.setState(prev => ({
            selectedLibrary: prev.selectedLibrary === library.url ? null : library.url
        }));
    }
    handleTagClick (tag) {
        // A built-in tag (All/Sound/Pen/...) was clicked; clear any library
        // filter so it behaves normally.
        if (this.state.selectedLibrary) {
            this.setState({selectedLibrary: null});
        }
    }
    handleChipRemove (index) {
        const libraries = this.state.libraries.slice();
        const removed = libraries[index];
        if (removed && removed.builtin) {
            return; // Built-in libraries cannot be removed.
        }
        libraries.splice(index, 1);
        this.setState(prev => ({
            libraries,
            selectedLibrary: prev.selectedLibrary === (removed && removed.url) ?
                null : prev.selectedLibrary
        }));
        writeLibraries(libraries);
    }
    render () {
        let library = null;
        if (this.state.selectedLibrary) {
            // A library chip is active: show ONLY that library's extensions,
            // just like clicking a built-in tag filters the gallery.
            const selectedLib = this.state.libraries.find(
                lib => lib.url === this.state.selectedLibrary
            );
            if (selectedLib && Array.isArray(selectedLib.extensions)) {
                library = selectedLib.extensions.map(toLibraryItem);
            }
        } else if (this.state.gallery || this.state.galleryError || this.state.galleryTimedOut) {
            library = extensionLibraryContent.map(toLibraryItem);
            library.push('---');
            if (this.state.gallery) {
                library.push(toLibraryItem(galleryMore));
                const locale = this.props.intl.locale;
                library.push(
                    ...this.state.gallery
                        .filter(i => i.extensionId !== 'faceSensing')
                        .map(i => translateGalleryItem(i, locale))
                        .map(toLibraryItem)
                );
            } else if (this.state.galleryError) {
                library.push(toLibraryItem(galleryError));
            } else {
                library.push(toLibraryItem(galleryLoading));
            }
        }

        // Append every added extension library's extensions as cards in the grid.
        if (!this.state.selectedLibrary && library && this.state.libraries.length > 0) {
            library.push('---');
            for (const lib of this.state.libraries) {
                if (!Array.isArray(lib.extensions)) {
                    continue;
                }
                library.push(...lib.extensions.map(toLibraryItem));
            }
        }

        const filterBarExtra = (
            <React.Fragment>
                {this.state.libraries.length > 0 && (
                    <div className={libraryStyles['library-chips']}>
                        {this.state.libraries.map((libraryItem, index) => (
                            <span
                                className={libraryStyles['library-chip-wrapper']}
                                key={libraryItem.url}
                                title={libraryItem.url}
                            >
                                <TagButton
                                    active={this.state.selectedLibrary === libraryItem.url ||
                                        (libraryItem.extensions || []).some(
                                            extension => this.state.loadedURLs.includes(extension.extensionURL)
                                        )}
                                    className={libraryStyles['library-chip']}
                                    tag={libraryItem.url}
                                    intlLabel={libraryItem.name}
                                    onClick={() => this.handleChipClick(libraryItem)}
                                />
                                <span
                                    className={libraryStyles['library-chip-remove']}
                                    onClick={event => {
                                        event.stopPropagation();
                                        this.handleChipRemove(index);
                                    }}
                                    role="button"
                                >
                                    {'×'}
                                </span>
                            </span>
                        ))}
                    </div>
                )}
                <TagButton
                    className={libraryStyles['add-extension-library-button']}
                    tag="add-extension-library"
                    intlLabel={{
                        defaultMessage: 'Add Extension Library',
                        description: 'Button in the extension library navigation bar to add an extension library by URL',
                        id: 'tw.extensionLibrary.addButton'
                    }}
                    onClick={this.handleOpenAddModal}
                />
            </React.Fragment>
        );

        return (
            <React.Fragment>
                <LibraryComponent
                    key={this.state.selectedLibrary || 'extensionLibrary-all'}
                    data={library}
                    vm={this.props.vm}
                    filterable
                    filterBarExtra={filterBarExtra}
                    persistableKey="extensionId"
                    id="extensionLibrary"
                    tags={extensionTags}
                    onTagClick={this.handleTagClick}
                    title={this.props.intl.formatMessage(messages.extensionTitle)}
                    visible={this.props.visible}
                    onItemSelected={this.handleItemSelect}
                    onRequestClose={this.props.onRequestClose}
                />
                {this.state.addModalOpen && (
                    <AddExtensionLibraryModal
                        url={this.state.url}
                        error={this.state.urlError}
                        onChangeURL={this.handleChangeURL}
                        onKeyDown={this.handleKeyDown}
                        onLoad={this.handleLoadLibrary}
                        onSelectLocal={this.handleSelectLocal}
                        onClose={this.handleCloseAddModal}
                        localName={this.state.localName}
                        onChangeLocalName={this.handleChangeLocalName}
                    />
                )}
            </React.Fragment>
        );
    }
}

ExtensionLibrary.propTypes = {
    intl: intlShape.isRequired,
    onCategorySelected: PropTypes.func,
    onEnableProcedureReturns: PropTypes.func,
    onOpenCustomExtensionModal: PropTypes.func,
    onRequestClose: PropTypes.func,
    visible: PropTypes.bool,
    vm: PropTypes.instanceOf(VM).isRequired // eslint-disable-line react/no-unused-prop-types
};

export default injectIntl(ExtensionLibrary);
